import React, { useState, useEffect, useContext } from 'react';
import SearchContext from '../../state/context';
import { unitConversion, sortResultsByDistance } from '../../helpers';

const ResultsColumn = props => {
  const {
    unit = 'mi',
    unitLabelCard = 'mi',
    levelsOfCareLabel = 'Types of Living',
    imagesDomain = '',
    pricingMessage = 'Room rates starting at [[communityPrice]]/month.*',
    pricingAvailabilityLabel = 'Pricing & Availability',
    residentsAndFamilyLabel = 'Residents & Family',
    onCloseCommunity,
    noResultsMessage = 'No results found'
  } = props;

  const {
    resultsListController,
    waitingForGeolocation,
    onCompareClick,
    comparedCommunities
  } = useContext(SearchContext);

  const [state, setState] = useState(resultsListController.state);
  const [displayedResults, setDisplayedResults] = useState([]);

  useEffect(() => {
    resultsListController.subscribe(() => {
      setState(resultsListController.state);
      if (resultsListController.state.results) {
        const originalResults = [...resultsListController.state.results];
        const sortedResults = sortResultsByDistance(originalResults);
        setDisplayedResults(sortedResults);
      }
    });
  }, []);

  if (waitingForGeolocation)
    return (
      <div className="headless-loader">
        <div className="headless-loader__inner"></div>
      </div>
    );

  const comparedCommunitiesIds = comparedCommunities.map(
    c => c.raw.ssl_communityid
  );

  if (!state || state.results.length === 0)
    return <div className="coveo-no-results-message">{noResultsMessage}</div>;

  return (
    <>
      {state &&
        state.results.length > 0 &&
        displayedResults.map((result, index) => (
          <div className="coveo-list-layout CoveoResult" key={index}>
            <div className="find-a-community-map-view-row__result-frame">
              <a
                className="find-a-community-map-view-row__result-card"
                data-community-id={result.raw.ssl_communityid}
                tabIndex="0"
                href={result.raw.uri}
              >
                <div className="find-a-community-map-view-row__result-card-info-column">
                  <div className="find-a-community-map-view-row__card-title">
                    {result.raw.ssl_communityname
                      ? result.raw.ssl_communityname
                      : result.raw.ssl_heading}
                  </div>
                  <div className="find-a-community-map-view-row__card-location-info">
                    {result.raw.ssl_city && (
                      <div>
                        <span>{result.raw.ssl_city}</span>
                        {result.raw.ssl_stateabbreviation && (
                          <span>, {result.raw.ssl_stateabbreviation}</span>
                        )}
                      </div>
                    )}
                    {result.raw.ssl_zipcode ? (
                      <div>{result.raw.ssl_zipcode}</div>
                    ) : (
                      <div>{result.raw.ssl_zipcode_i}</div>
                    )}
                    {(result.raw.distance_search || result.raw.distance) && (
                      <div className="find-a-community-map-view-row__card-distance">
                        {result.raw.distance_search
                          ? unitConversion(result.raw.distance_search, unit)
                          : unitConversion(result.raw.distance, unit)}
                        <span> {unitLabelCard}</span>
                      </div>
                    )}
                  </div>
                  {result.raw.ssl_levelsofcare && (
                    <div className="find-a-community-map-view-row__card-levels-of-care">
                      <h3>{levelsOfCareLabel.toUpperCase()}</h3>
                      <p>
                        {result.raw.ssl_levelsofcare.map(
                          (level, index) =>
                            `${level}${
                              index < result.raw.ssl_levelsofcare.length - 1
                                ? ', '
                                : ''
                            }`
                        )}
                      </p>
                    </div>
                  )}
                  {(result.raw.ssl_pricingandavailabilityphone ||
                    result.raw.ssl_residentsandfamilyphone) && (
                    <div className="find-a-community-map-view-row__card-phone-info">
                      {result.raw.ssl_pricingandavailabilityphone && (
                        <p>
                          <span>{pricingAvailabilityLabel}</span>
                          <span>
                            {result.raw.ssl_pricingandavailabilityphone}
                          </span>
                        </p>
                      )}
                      {result.raw.ssl_residentsandfamilyphone && (
                        <p>
                          <span>{residentsAndFamilyLabel}</span>
                          <span>{result.raw.ssl_residentsandfamilyphone}</span>
                        </p>
                      )}
                    </div>
                  )}
                  {result.raw.ssl_communityprice && (
                    <div className="find-a-community-map-view-row__pricing-info">
                      <p>
                        {pricingMessage.split('[[communityPrice]]')[0]}
                        <span>{result.raw.ssl_communityprice}</span>
                        {pricingMessage.split('[[communityPrice]]')[1]}
                      </p>
                    </div>
                  )}
                </div>
                <div className="find-a-community-map-view-row__result-card-image-column">
                  <div className="find-a-community-map-view-row__result-card-image-container">
                    {result.raw.ssl_thumbnailimage ? (
                      <img
                        src={`${imagesDomain}${result.raw.ssl_thumbnailimage}`}
                      />
                    ) : (
                      <>
                        {result.raw.ssl_mainimagedesktop && (
                          <img
                            src={`${imagesDomain}${result.raw.ssl_mainimagedesktop}`}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </a>
              <div className="find-a-community-map-view-row__image-compare-control">
                <label
                  className={`find-a-community-map-view-row__checkbox-label ${
                    comparedCommunities.length >= 3
                      ? 'find-a-community-map-view-row__checkbox-label--disabled'
                      : ''
                  }`}
                >
                  <input
                    disabled={comparedCommunities.length >= 3}
                    type="checkbox"
                    data-community-id={result.raw.ssl_communityid}
                    className="find-a-community-map-view-row__checkbox"
                    value="Compare"
                    aria-label="Compare"
                    aria-hidden="true"
                    tabIndex="-1"
                    checked={comparedCommunitiesIds.includes(
                      result.raw.ssl_communityid
                    )}
                    onChange={() => {
                      onCompareClick(result);
                    }}
                  />
                  <button
                    className="find-a-community-map-view-row__checkbox-button"
                    aria-label="Compare"
                  ></button>
                  <span title="Compare" aria-hidden="true">
                    Compare
                  </span>
                </label>
              </div>
            </div>
          </div>
        ))}
    </>
  );
};

export default ResultsColumn;
